<template>
  <div>
    <div class="toolbar">
      <div>
        <h2>城市管理</h2>
        <p class="hint">用户需先选城市，才能浏览该城市下的分类与商家</p>
      </div>
      <el-button type="primary" @click="openForm()">新增</el-button>
    </div>
    <div class="filters">
      <el-input
        v-model="keyword"
        placeholder="搜索名称（中/缅）"
        clearable
        prefix-icon="el-icon-search"
        class="filter-input"
      />
      <el-radio-group v-model="sortAsc" size="small">
        <el-radio-button :label="true">正序</el-radio-button>
        <el-radio-button :label="false">倒序</el-radio-button>
      </el-radio-group>
    </div>
    <div v-if="!isMobile" class="table-scroll">
      <el-table :data="displayList" v-loading="loading" border>
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="name_zh" label="中文名" />
        <el-table-column prop="name_my" label="缅文名" />
        <el-table-column prop="sort_order" label="排序" width="80" />
        <el-table-column label="展示" width="80">
          <template slot-scope="{ row }">
            <el-tag :type="row.is_active ? 'success' : 'info'" size="mini">
              {{ row.is_active ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160">
          <template slot-scope="{ row }">
            <el-button type="text" @click="openForm(row)">编辑</el-button>
            <el-button type="text" style="color:#f56c6c" @click="onDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <MobileCardList
      v-if="isMobile"
      :data="displayList"
      :loading="loading"
      :fields="cardFields"
    >
      <template #is_active="{ row }">
        <el-tag :type="row.is_active ? 'success' : 'info'" size="mini">
          {{ row.is_active ? '是' : '否' }}
        </el-tag>
      </template>
      <template #actions="{ row }">
        <el-button type="text" @click="openForm(row)">编辑</el-button>
        <el-button type="text" style="color:#f56c6c" @click="onDelete(row)">删除</el-button>
      </template>
    </MobileCardList>

    <el-dialog :title="form.id ? '编辑城市' : '新增城市'" :visible.sync="visible" width="480px">
      <el-form :model="form" label-width="90px">
        <el-form-item label="中文名"><el-input v-model="form.name_zh" /></el-form-item>
        <el-form-item label="缅文名"><el-input v-model="form.name_my" /></el-form-item>
        <el-form-item label="排序"><el-input-number v-model="form.sort_order" :min="1" /></el-form-item>
        <el-form-item label="展示"><el-switch v-model="form.is_active" :active-value="1" :inactive-value="0" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" @click="onSubmit">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { api } from '../api';
import { calcNextSortOrder, filterByKeyword, sortByOrder } from '../utils/listHelpers';
import responsiveMixin from '../mixins/responsive';

const emptyForm = () => ({
  id: null, name_zh: '', name_my: '', sort_order: 1, is_active: 1,
});

export default {
  name: 'Cities',
  mixins: [responsiveMixin],
  data() {
    return {
      list: [],
      loading: false,
      visible: false,
      form: emptyForm(),
      keyword: '',
      sortAsc: true,
      cardFields: [
        { label: 'ID', prop: 'id' },
        { label: '中文名', prop: 'name_zh' },
        { label: '缅文名', prop: 'name_my' },
        { label: '排序', prop: 'sort_order' },
        { label: '展示', prop: 'is_active' },
      ],
    };
  },
  computed: {
    displayList() {
      return sortByOrder(filterByKeyword(this.list, this.keyword), this.sortAsc);
    },
  },
  created() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await api.cities.list();
        this.list = data;
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.loading = false;
      }
    },
    openForm(row) {
      if (row) {
        this.form = { ...row };
      } else {
        this.form = {
          ...emptyForm(),
          sort_order: calcNextSortOrder(this.list),
        };
      }
      this.visible = true;
    },
    onSubmit() {
      const action = this.form.id ? '保存修改' : '新增';
      this.$confirm(`确认${action}该城市？`, '提示', { type: 'warning' })
        .then(async () => {
          try {
            if (this.form.id) {
              await api.cities.update(this.form.id, this.form);
            } else {
              await api.cities.create(this.form);
            }
            this.$message.success('操作成功');
            this.visible = false;
            this.load();
          } catch (e) {
            this.$message.error(e.message);
          }
        })
        .catch(() => {});
    },
    onDelete(row) {
      this.$confirm(`确认删除城市「${row.name_zh}」？`, '删除确认', {
        type: 'warning',
        confirmButtonText: '确认删除',
        confirmButtonClass: 'el-button--danger',
      })
        .then(async () => {
          try {
            await api.cities.remove(row.id);
            this.$message.success('已删除');
            this.load();
          } catch (e) {
            this.$message.error(e.message);
          }
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.toolbar h2 { margin: 0; }
.hint { margin: 4px 0 0; font-size: 13px; color: #909399; }
.filters { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
.filter-input { width: 220px; }
</style>
